/**
 * Alta de credencial. La edición es EditSecretDialog (metadata, notas y TOTP); la contraseña se cambia con Rotar.
 * Exige el Vault desbloqueado antes de mandar (ensureUnlocked).
 */
import { useEffect, useMemo, useState } from 'react';
import { Wand2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useVaultMutations, withUnlockRetry } from '@/hooks/useVault';
import { VAULT_AREAS, VAULT_POLICY, parsePrincipal } from '../../../../shared/types/vault';

const NONE = '__none__';

function generatePassword(length = 20): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&*-_+=?';
  const bytes = new Uint32Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
}

interface SecretFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** principals de quien crea: las áreas propias van primero en el selector */
  principals: string[];
  isAdmin: boolean;
  ensureUnlocked: () => Promise<boolean>;
  onCreated: (secretId: string) => void;
}

export function SecretFormDialog({ open, onOpenChange, principals, isAdmin, ensureUnlocked, onCreated }: SecretFormDialogProps) {
  const { t } = useTranslation();
  const { createSecret } = useVaultMutations();

  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [url, setUrl] = useState('');
  const [area, setArea] = useState<string>(NONE);
  const [clientId, setClientId] = useState('');
  const [tags, setTags] = useState('');
  const [notes, setNotes] = useState('');
  const [totpSeed, setTotpSeed] = useState('');
  const [shareWithArea, setShareWithArea] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(''); setUsername(''); setPassword(''); setUrl(''); setArea(NONE);
    setClientId(''); setTags(''); setNotes(''); setTotpSeed(''); setShareWithArea(true); setShowPassword(false);
  }, [open]);

  const myAreas = useMemo(
    () => principals.map(parsePrincipal).filter((p): p is { kind: 'area'; group: string } => !!p && p.kind === 'area').map((p) => p.group),
    [principals]
  );
  const areaOptions = isAdmin ? VAULT_AREAS.map((a) => a.id) : myAreas;

  const canSave = name.trim() && username.trim() && password && !createSecret.isPending;

  const submit = async () => {
    // el toast de error lo pone onError del mutation; acá el reintento por VAULT_LOCKED y nada sin manejar
    const created = await withUnlockRetry(ensureUnlocked, () => createSecret.mutateAsync({
      name: name.trim(),
      username: username.trim(),
      password,
      url: url.trim() || undefined,
      area: area === NONE ? undefined : area,
      clientId: clientId.trim() || undefined,
      tags: tags.split(',').map((s) => s.trim()).filter(Boolean).slice(0, 10),
      notes: notes.trim() || undefined,
      totpSeed: totpSeed.trim() || undefined,
      shareWithArea: area === NONE ? undefined : shareWithArea,
    })).catch(() => null);
    if (!created) return;
    onOpenChange(false);
    onCreated(created.secretId);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('vault.form.title', 'Nueva credencial')}</DialogTitle>
          <DialogDescription>{t('vault.form.sub', 'Vos quedás como owner. Si elegís un área, su grupo la ve en modo lectura.')}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 py-2">
          <div className="space-y-1">
            <Label htmlFor="s-name">{t('vault.form.name', 'Nombre')}</Label>
            <Input id="s-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('vault.form.name_ph', 'Meta Business — LATAM Airlines')} maxLength={120} autoFocus />
          </div>
          <div className="space-y-1">
            <Label htmlFor="s-user">{t('vault.form.username', 'Usuario / email')}</Label>
            <Input id="s-user" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="accounts@winclap.com" maxLength={200} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="s-pass">{t('vault.form.password', 'Contraseña')}</Label>
            <div className="flex gap-2">
              <Input id="s-pass" type={showPassword ? 'text' : 'password'} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} className="font-mono" maxLength={1024} />
              <Button type="button" variant="outline" onClick={() => { setPassword(generatePassword()); setShowPassword(true); }} title={t('vault.form.generate', 'Generar')} aria-label={t('vault.form.generate', 'Generar')}>
                <Wand2 className="h-4 w-4" />
              </Button>
            </div>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <Checkbox checked={showPassword} onCheckedChange={(v) => setShowPassword(v === true)} />
              {t('vault.form.show_password', 'Mostrar')}
            </label>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>{t('vault.form.area', 'Área')}</Label>
              <Select value={area} onValueChange={setArea}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t('vault.form.area_none', 'Sin área')}</SelectItem>
                  {areaOptions.map((id) => (
                    <SelectItem key={id} value={id}>{VAULT_AREAS.find((a) => a.id === id)?.label ?? id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="s-client">{t('vault.form.client', 'Cliente')}</Label>
              <Input id="s-client" value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="latam" maxLength={40} />
            </div>
          </div>
          {area !== NONE && (
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={shareWithArea} onCheckedChange={(v) => setShareWithArea(v === true)} />
              {t('vault.form.share_area', 'Compartir en modo lectura con todo el grupo del área')}
            </label>
          )}
          <div className="space-y-1">
            <Label htmlFor="s-url">URL</Label>
            <Input id="s-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://business.facebook.com/settings" maxLength={500} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="s-totp">{t('vault.form.totp', 'Semilla TOTP (opcional)')}</Label>
            <Input id="s-totp" value={totpSeed} onChange={(e) => setTotpSeed(e.target.value)} placeholder={t('vault.form.totp_ph', 'base32 u otpauth://totp/… — el código se genera dentro del Vault')} className="font-mono" maxLength={400} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="s-tags">{t('vault.form.tags', 'Etiquetas (separadas por coma)')}</Label>
            <Input id="s-tags" value={tags} onChange={(e) => setTags(e.target.value)} placeholder={t('vault.form.tags_ph', 'cuenta-compartida, meta')} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="s-notes">{t('vault.form.notes', 'Notas (cifradas)')}</Label>
            <Textarea id="s-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={VAULT_POLICY.notesMaxChars} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.ui.cancel', 'Cancelar')}</Button>
          <Button onClick={submit} disabled={!canSave}>
            {createSecret.isPending ? t('vault.form.saving', 'Guardando…') : t('vault.form.create', 'Crear credencial')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
